import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Heart, Scale, Sun, Moon, Globe, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useFavorites } from '@/context/FavoritesContext';
import { useTranslatedSettings } from '@/context/SiteSettingsContext';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon (not in lucide-react)
const TikTokIcon = ({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// X (Twitter) icon
const XIcon = ({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { count } = useFavorites();
  const { settings, loading } = useTranslatedSettings();
  const { darkMode, toggleDarkMode, isDarkModeAllowed, isInherentlyDark } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const [compareCount, setCompareCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle anchor links - navigate to homepage first if not on homepage
  const handleAnchorClick = (e, anchor) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      // Navigate to homepage with the anchor
      navigate('/' + anchor);
    } else {
      // Already on homepage, just scroll to anchor
      const element = document.querySelector(anchor);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    // Check comparison count
    const sessionId = localStorage.getItem('comparison_session_id');
    if (sessionId) {
      fetch(`${API_URL}/api/compare/${sessionId}`)
        .then(res => res.json())
        .then(data => setCompareCount(data.cars?.length || 0))
        .catch(() => {});
    }
  }, []);

  const s = settings || {};
  const showDarkModeToggle = isDarkModeAllowed() && !isInherentlyDark();
  
  // Header icon color from settings (default white for dark headers)
  const headerIconColor = s.design_header_icon_color || '#FFFFFF';
  // Whether to invert logo in dark mode
  const logoInvertDark = s.design_logo_invert_dark || false;
  // Header settings
  const headerSticky = s.design_header_sticky !== false;
  const headerStyle = s.design_header_style || 'transparent';
  const headerBgColor = s.design_header_bg_color || '#0A0A0A';
  const headerBlur = s.design_header_blur !== false;

  // Build header classes and styles
  const getHeaderClasses = () => {
    let classes = 'left-0 right-0 z-50 border-b border-theme';
    if (headerSticky) {
      classes += ' fixed top-0';
    }
    if (headerStyle === 'transparent' && headerBlur) {
      classes += ' backdrop-blur-md';
    }
    return classes;
  };

  const getHeaderStyle = () => {
    if (headerStyle === 'solid') {
      return { backgroundColor: headerBgColor };
    }
    // Transparent with opacity
    return { backgroundColor: `${headerBgColor}CC` };
  };

  return (
    <nav className={getHeaderClasses()} style={getHeaderStyle()}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            {s.logo_url ? (
              <img 
                src={getImageUrl(s.logo_url)} 
                alt={s.site_name || 'Logo'} 
                className={`h-10 md:h-12 ${darkMode && logoInvertDark ? 'brightness-0 invert' : ''}`} 
              />
            ) : (
              <span className="font-heading text-xl md:text-2xl font-light tracking-tight text-theme">
                {(s.site_name || 'LUXURY').split(' ')[0]}<span className="text-gold">{(s.site_name || 'RIDES').split(' ')[1] || 'RIDES'}</span>
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity"
              style={{ color: headerIconColor }}
              data-testid="nav-home"
            >
              {s.nav_home || t('nav_home')}
            </Link>
            <Link 
              to="/cars" 
              className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity"
              style={{ color: headerIconColor }}
              data-testid="nav-inventory"
            >
              {s.nav_inventory || t('nav_inventory')}
            </Link>
            <Link 
              to="/verkaufte-fahrzeuge" 
              className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity"
              style={{ color: headerIconColor }}
              data-testid="nav-sold-cars"
            >
              {s.nav_sold_cars || t('nav_sold_cars')}
            </Link>
            <Link 
              to="/wir-finden" 
              className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity"
              style={{ color: headerIconColor }}
              data-testid="nav-we-find"
            >
              {s.nav_we_find || t('nav_we_find')}
            </Link>
            {/* Compare Link - only if enabled */}
            {s.module_compare_enabled !== false && (
              <Link 
                to="/compare" 
                className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity"
                style={{ color: headerIconColor }}
                data-testid="nav-compare"
              >
                {s.nav_compare || 'Vergleich'}
              </Link>
            )}
            {/* Team Link - only if enabled */}
            {s.module_team_enabled && (
              <Link 
                to="/team" 
                className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity"
                style={{ color: headerIconColor }}
                data-testid="nav-team"
              >
                {s.nav_team || 'Team'}
              </Link>
            )}
            {/* Blog Link - only if enabled */}
            {s.module_blog_enabled && (
              <Link 
                to="/blog" 
                className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity"
                style={{ color: headerIconColor }}
                data-testid="nav-blog"
              >
                {s.nav_blog || 'News'}
              </Link>
            )}
            {/* Test Drive Link - only if enabled */}
            {s.module_test_drive_enabled && (
              <Link 
                to="/test-drive" 
                className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity"
                style={{ color: headerIconColor }}
                data-testid="nav-test-drive"
              >
                {language === 'de' ? 'Probefahrt' : 'Test Drive'}
              </Link>
            )}
            <a 
              href="#about" 
              onClick={(e) => handleAnchorClick(e, '#about')}
              className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity cursor-pointer"
              style={{ color: headerIconColor }}
              data-testid="nav-about"
            >
              {s.nav_about || t('nav_about')}
            </a>
            <a 
              href="#contact" 
              onClick={(e) => handleAnchorClick(e, '#contact')}
              className="text-sm uppercase tracking-[0.15em] hover:opacity-70 transition-opacity cursor-pointer"
              style={{ color: headerIconColor }}
              data-testid="nav-contact"
            >
              {s.nav_contact || t('nav_contact')}
            </a>
          </div>

          {/* Right Side - Language, Theme Toggle, Compare, Wishlist & CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-2 py-1 text-sm hover:opacity-70 transition-opacity border rounded"
              style={{ color: headerIconColor, borderColor: `${headerIconColor}40` }}
              data-testid="language-toggle"
              title={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
            >
              <Globe size={16} />
              <span className="font-medium">{language.toUpperCase()}</span>
            </button>
            
            {/* Dark Mode Toggle */}
            {showDarkModeToggle && (
              <button
                onClick={toggleDarkMode}
                className="p-2 hover:opacity-70 transition-opacity"
                style={{ color: headerIconColor }}
                data-testid="dark-mode-toggle"
                title={darkMode ? 'Hellmodus' : 'Dunkelmodus'}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            )}
            
            {/* Compare - only if enabled */}
            {s.module_compare_enabled !== false && (
              <Link 
                to="/compare" 
                className="relative p-2 hover:opacity-70 transition-opacity"
                style={{ color: headerIconColor }}
                data-testid="nav-compare"
              >
                <Scale size={22} />
                {compareCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-theme text-theme-surface text-xs flex items-center justify-center rounded-full">
                    {compareCount}
                  </span>
                )}
              </Link>
            )}
            
            {/* Favorites - only if enabled */}
            {s.module_favorites_enabled !== false && (
              <Link 
                to="/favorites" 
                className="relative p-2 hover:opacity-80 transition-opacity"
                style={{ color: headerIconColor }}
                data-testid="nav-wishlist"
              >
                <Heart size={22} />
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-gold text-white text-xs flex items-center justify-center rounded-full">
                    {count}
                  </span>
                )}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            {/* Mobile Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="px-2 py-1 text-xs font-medium border rounded"
              style={{ color: headerIconColor, borderColor: `${headerIconColor}40` }}
              data-testid="language-toggle-mobile"
            >
              {language.toUpperCase()}
            </button>
            {showDarkModeToggle && (
              <button
                onClick={toggleDarkMode}
                className="p-2 hover:opacity-80 transition-opacity"
                style={{ color: headerIconColor }}
                data-testid="dark-mode-toggle-mobile"
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            )}
            <button 
              className="p-2 hover:opacity-80 transition-opacity"
              style={{ color: headerIconColor }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-theme-surface border-t border-theme">
          <div className="px-6 py-6 space-y-4">
            <Link 
              to="/" 
              className="block text-sm uppercase tracking-[0.15em] text-theme-muted"
              onClick={() => setMobileMenuOpen(false)}
            >
              {s.nav_home || t('nav_home')}
            </Link>
            <Link 
              to="/cars" 
              className="block text-sm uppercase tracking-[0.15em] text-theme-muted"
              onClick={() => setMobileMenuOpen(false)}
            >
              {s.nav_inventory || t('nav_inventory')}
            </Link>
            <Link 
              to="/verkaufte-fahrzeuge" 
              className="block text-sm uppercase tracking-[0.15em] text-theme-muted"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-sold-cars-mobile"
            >
              {s.nav_sold_cars || t('nav_sold_cars')}
            </Link>
            <Link 
              to="/wir-finden" 
              className="block text-sm uppercase tracking-[0.15em] text-theme-muted"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-we-find-mobile"
            >
              {s.nav_we_find || t('nav_we_find')}
            </Link>
            <Link 
              to="/compare" 
              className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-theme-muted"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Scale size={16} />
              {t('nav_compare')} {compareCount > 0 && `(${compareCount})`}
            </Link>
            <Link 
              to="/favorites" 
              className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-theme-muted"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Heart size={16} />
              {s.nav_favorites || t('nav_favorites')} {count > 0 && `(${count})`}
            </Link>
            {/* Team Link - only if enabled */}
            {s.module_team_enabled && (
              <Link 
                to="/team" 
                className="block text-sm uppercase tracking-[0.15em] text-theme-muted"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="nav-team-mobile"
              >
                {s.nav_team || 'Team'}
              </Link>
            )}
            {/* Blog Link - only if enabled */}
            {s.module_blog_enabled && (
              <Link 
                to="/blog" 
                className="block text-sm uppercase tracking-[0.15em] text-theme-muted"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="nav-blog-mobile"
              >
                {s.nav_blog || 'News'}
              </Link>
            )}
            {/* Test Drive Link - only if enabled */}
            {s.module_test_drive_enabled && (
              <Link 
                to="/test-drive" 
                className="block text-sm uppercase tracking-[0.15em] text-theme-muted"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="nav-test-drive-mobile"
              >
                {language === 'de' ? 'Probefahrt' : 'Test Drive'}
              </Link>
            )}
            <a 
              href="#about" 
              className="block text-sm uppercase tracking-[0.15em] text-theme-muted cursor-pointer"
              onClick={(e) => handleAnchorClick(e, '#about')}
            >
              {s.nav_about || t('nav_about')}
            </a>
            <a 
              href="#contact" 
              className="block text-sm uppercase tracking-[0.15em] text-theme-muted cursor-pointer"
              onClick={(e) => handleAnchorClick(e, '#contact')}
            >
              {s.nav_contact || t('nav_contact')}
            </a>
            <Link 
              to="/cars" 
              className="btn-primary inline-block mt-4"
              onClick={() => setMobileMenuOpen(false)}
            >
              {s.nav_inventory || t('nav_inventory')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  const { settings } = useTranslatedSettings();
  const { darkMode } = useTheme();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const s = settings || {};

  // Handle anchor links - navigate to homepage first if not on homepage
  const handleAnchorClick = (e, anchor) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/' + anchor);
    } else {
      const element = document.querySelector(anchor);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Social Media Links (only show if filled)
  const socialLinks = [
    { key: 'social_facebook', url: s.social_facebook, icon: 'facebook', label: 'Facebook' },
    { key: 'social_instagram', url: s.social_instagram, icon: 'instagram', label: 'Instagram' },
    { key: 'social_tiktok', url: s.social_tiktok, icon: 'tiktok', label: 'TikTok' },
    { key: 'social_youtube', url: s.social_youtube, icon: 'youtube', label: 'YouTube' },
    { key: 'social_linkedin', url: s.social_linkedin, icon: 'linkedin', label: 'LinkedIn' },
    { key: 'social_x', url: s.social_x, icon: 'x', label: 'X' },
  ].filter(link => link.url);

  const SocialIcon = ({ type }) => {
    switch (type) {
      case 'facebook':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
      case 'instagram':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>;
      case 'tiktok':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>;
      case 'youtube':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
      case 'linkedin':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>;
      case 'x':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>;
      default:
        return null;
    }
  };

  return (
    <footer className="bg-theme-surface py-16 md:py-20 border-t border-theme">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            {s.logo_url ? (
              <img 
                src={getImageUrl(s.logo_url)} 
                alt={s.site_name || 'Logo'} 
                className={`h-10 mb-4 ${darkMode ? '' : ''}`}
              />
            ) : (
              <span className="font-heading text-2xl font-light tracking-tight text-theme">
                {(s.site_name || 'LUXURY').split(' ')[0]}<span className="text-gold">{(s.site_name || 'RIDES').split(' ')[1] || 'RIDES'}</span>
              </span>
            )}
            <p className="text-theme-muted text-sm mt-4 leading-relaxed">
              {s.footer_description || 'Ihr Premier-Ziel für exotische und luxuriöse Fahrzeuge. Erleben Sie automobile Exzellenz.'}
            </p>
            
            {/* Social Media Icons */}
            {socialLinks.length > 0 && (
              <div className="flex gap-3 mt-6">
                {socialLinks.map((social) => (
                  <a
                    key={social.key}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-theme border border-theme flex items-center justify-center text-theme-muted hover:text-primary hover:border-primary transition-colors"
                    title={social.label}
                    data-testid={`social-${social.icon}`}
                  >
                    <SocialIcon type={social.icon} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="overline text-theme mb-6">{t('footer_navigation')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-theme-muted text-sm hover:text-primary transition-colors">
                  {s.nav_home || t('nav_home')}
                </Link>
              </li>
              <li>
                <Link to="/cars" className="text-theme-muted text-sm hover:text-primary transition-colors">
                  {s.nav_inventory || t('nav_inventory')}
                </Link>
              </li>
              <li>
                <Link to="/verkaufte-fahrzeuge" className="text-theme-muted text-sm hover:text-primary transition-colors">
                  {s.nav_sold_cars || t('nav_sold_cars')}
                </Link>
              </li>
              <li>
                <Link to="/wir-finden" className="text-theme-muted text-sm hover:text-primary transition-colors">
                  {s.nav_we_find || t('nav_we_find')}
                </Link>
              </li>
              {s.module_team_enabled && (
                <li>
                  <Link to="/team" className="text-theme-muted text-sm hover:text-primary transition-colors">
                    {s.nav_team || 'Team'}
                  </Link>
                </li>
              )}
              {s.module_blog_enabled && (
                <li>
                  <Link to="/blog" className="text-theme-muted text-sm hover:text-primary transition-colors">
                    {s.nav_blog || 'News'}
                  </Link>
                </li>
              )}
              <li>
                <Link to="/compare" className="text-theme-muted text-sm hover:text-primary transition-colors">
                  {t('nav_compare')}
                </Link>
              </li>
              <li>
                <a 
                  href="#about" 
                  onClick={(e) => handleAnchorClick(e, '#about')}
                  className="text-theme-muted text-sm hover:text-primary transition-colors cursor-pointer"
                >
                  {s.nav_about || t('nav_about')}
                </a>
              </li>
              <li>
                <a 
                  href="#contact" 
                  onClick={(e) => handleAnchorClick(e, '#contact')}
                  className="text-theme-muted text-sm hover:text-primary transition-colors cursor-pointer"
                >
                  {s.nav_contact || t('nav_contact')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="overline text-theme mb-6">{t('footer_contact')}</h4>
            <ul className="space-y-3 text-theme-muted text-sm">
              <li>{s.contact_address || 'Musterstraße 123, 1010 Wien'}</li>
              <li>{s.contact_phone || '+43 1 234 5678'}</li>
              <li>{s.contact_email || 'info@tim-automobile.at'}</li>
            </ul>
            {/* Small clickable Google Maps preview */}
            {s.contact_address && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.contact_address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 block rounded-md overflow-hidden border border-theme hover:opacity-90 transition-opacity relative group"
                style={{ height: '140px' }}
                data-testid="footer-map-mini"
                title={language === 'de' ? 'Route planen' : 'Get directions'}
              >
                <iframe
                  title="Google Maps"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(s.contact_address)}&z=15&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: 'block', pointerEvents: 'none' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-end justify-end p-2">
                  <span className="text-[10px] uppercase tracking-wider bg-black/70 text-white px-2 py-1 rounded">
                    {language === 'de' ? 'Route planen' : 'Directions'}
                  </span>
                </div>
              </a>
            )}
          </div>

          {/* Hours */}
          <div>
            <h4 className="overline text-theme mb-6">{t('footer_hours')}</h4>
            <ul className="space-y-3 text-theme-muted text-sm">
              <li>{s.footer_hours_weekday}</li>
              <li>{s.footer_hours_saturday}</li>
              <li>{s.footer_hours_sunday}</li>
            </ul>
          </div>
        </div>


        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-theme-muted text-xs">
            © {new Date().getFullYear()} {s.footer_copyright || 'Tim Automobile'}. {t('footer_rights')}
          </p>
          
          <div className="flex gap-6">
            <Link to="/datenschutz" className="text-theme-muted text-xs hover:text-primary transition-colors">
              {t('footer_privacy')}
            </Link>
            <Link to="/agb" className="text-theme-muted text-xs hover:text-primary transition-colors">
              {t('footer_terms')}
            </Link>
            <Link to="/impressum" className="text-theme-muted text-xs hover:text-primary transition-colors">
              {t('footer_imprint')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Layout wrapper component for pages
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-theme-surface">
      <Navbar />
      {children}
      <Footer />
    </div>
  );
};

export default Layout;
